import { useFormik } from 'formik';
import { useState } from 'react';
import { useContextSelector } from 'use-context-selector';
import { CartContext } from '../../contexts/CartContext';
import { ModalsContext } from '../../contexts/ModalsContext';
import { ModuleContext } from '../../contexts/ModuleContext';
import { PatientContext, PatientProps } from '../../contexts/PatientContext';
import { PaymentContext } from '../../contexts/PaymentContext';
import { RouterContext } from '../../contexts/RouterContext';
import { ScheduleContext } from '../../contexts/ScheduleContext';
import { useGenericRulesCatalog } from '../../hooks/useGenericRulesCatalog';
import { usePatientModule } from '../../hooks/usePatientModule';
import { ProductItemProps, Rule } from '../../models/Product';
import { patientSchema } from '../../Schemas/patient';
import { INFO_GENERIC_RULES_CONDITION, INFO_GENERIC_RULES_WHEN } from '../../utils/constants';
import { handleGenericModals, verifyGenericModalCondition } from '../../utils/genericModals';
import { maskCpf, maskDateStringIso, maskPassport, maskPhone, removeMask } from '../../utils/masks';
import { isOlderThan } from '../../utils/validators';
import { INITIAL_VALUES, formPatientHasError } from './utils/validateForm';
import { ServiceContext } from '../../contexts/ServiceContext';
import { FirebaseEvents } from '../../utils/enums/firebase-events';
import { useAnalytics } from '../../hooks/useAnalytics';
import { envConfig } from '../../utils/enviroment';
import { validateApsEligibility } from '../../modules/products';
import { PAGES } from '../../Router/pages';
import { useProducts } from '../../flows/APS/store/Products';

export default function PatientIdentificationViewModel() {
  const { goTo } = useContextSelector(RouterContext, state => state);
  const { openModal } = useContextSelector(ModalsContext, state => state);
  const { changeProduct, findProductOriginal, productList } = useContextSelector(CartContext, state => state);
  const { setEligibilityPlans, eligibilityPlans } = useContextSelector(PaymentContext, state => state);
  const {
    selectedItem: scheduleSelected,
    setSelectedItem: changeSchedule,
    scheduleSpecialistSelected,
    setScheduleSpecialistSelected,
    changeScheduleSpecialist
  } = useContextSelector(ScheduleContext, state => state);
  const { addPatientList, setPatientSelected, patientSelected } = useContextSelector(PatientContext, state => state);
  const { selectedProduct, handleSelectProduct, selectedItemToSchedule } = useContextSelector(
    ModuleContext,
    state => state
  );
  const { selectedService } = useContextSelector(ServiceContext, state => state);

  const [loading, setLoading] = useState(false);
  const { hasGenericRuleCatalog, validateGenericRules } = useGenericRulesCatalog();

  const { setProducts: setApsProducts } = useProducts();

  const {
    handleAddPatient,
    genericModal,
    isModalSystemUnavailableVisible,
    setIsModalSystemUnavailableVisible,
    isModalProductNotCoveredVisible,
    setIsModalProductNotCoveredVisible,
    isModalNoEligibility,
    setIsModalNoEligibility,
    createSchedule
  } = usePatientModule({
    addPatientList,
    changeProduct,
    eligibilityPlans,
    setEligibilityPlans,
    goTo,
    changeSchedule: schedule => {
      changeSchedule(schedule);
      return schedule;
    },
    selectedSchedule: scheduleSelected,
    openModal,
    scheduleSpecialistSelected,
    setScheduleSpecialistSelected,
    changeScheduleSpecialist
  });

  const formik = useFormik({
    initialValues: INITIAL_VALUES,
    validationSchema: patientSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async values => {
      setLoading(true);
      const isFormValid = !formPatientHasError({
        values,
        formik,
        checkGenericRulesCatalog: (ruleCondition: string) =>
          hasGenericRuleCatalog(ruleCondition, selectedProduct?.rules)
      });

      const patient = handleRawPatientData(values);

      if (isFormValid && selectedItemToSchedule === 'SPECIALIST') {
        await handleAddPatient({
          productSelected: selectedProduct,
          itemToSchedule: selectedItemToSchedule,
          patient
        });
      }

      if (selectedService.id === envConfig.REACT_APP_SERVICE_ID) {
        setPatientSelected(patient);
        await checkPatientApsServices({ document: patient.document, documentType: patient.documentType });
      } else if (
        selectedItemToSchedule === 'PRODUCT' ||
        selectedItemToSchedule === 'SERVICE_ENV' ||
        selectedItemToSchedule === 'PRODUCT_ENV'
      ) {
        if (isFormValid && selectedProduct) {
          setPatientSelected(patient);

          await routeProductSelector({ product: selectedProduct, patient });
        }
      }

      setLoading(false);
    }
  });

  function handleRawPatientData(patientData: typeof INITIAL_VALUES): PatientProps {
    const patient = {} as PatientProps;
    patient.name = patientData.name.trim();
    patient.email = patientData.email.trim();

    patient.document = removeMask(patientData.document);
    patient.phone = removeMask(patientData.phone);

    if (!isOlderThan(patientData.birthDate, 9)) patient.pregnant = false;
    else patient.pregnant = patientData.pregnant === 'Sim';

    return { ...patientData, ...patient };
  }

  function checkMandatoryModal({ productRules, genericModalWhen }: { productRules: Rule[]; genericModalWhen: string }) {
    const genericModals = handleGenericModals(productRules);
    if (genericModals) {
      const modal = verifyGenericModalCondition({
        genericModals: genericModals,
        condition: INFO_GENERIC_RULES_CONDITION.always,
        when: genericModalWhen
      });
      if (modal) {
        genericModal.setGenericModalCondition(INFO_GENERIC_RULES_CONDITION.always);
        genericModal.setModalGenericVisible(true);
        return true;
      }
    }
    return false;
  }

  async function handleSchedulePatient({
    product,
    patient,
    showGenericModal
  }: {
    product: ProductItemProps;
    patient: PatientProps;
    showGenericModal: boolean;
  }) {
    setLoading(true);
    handleSelectProduct(product);
    const hasModal = await createSchedule({
      product,
      patient,
      itemToSchedule: 'PRODUCT'
    });
    if (!showGenericModal || hasModal) {
      if (hasModal?.type === INFO_GENERIC_RULES_CONDITION.is_first_appointment) {
        genericModal.setGenericModalCondition(INFO_GENERIC_RULES_CONDITION.is_first_appointment);
        genericModal.setModalGenericVisible(true);
      }
    }
    setLoading(false);
  }

  async function routeProductSelector({
    product,
    showGenericModal,
    patient
  }: {
    product: ProductItemProps;
    showGenericModal?: boolean;
    patient: PatientProps;
  }) {
    setLoading(true);
    let mandatoryModal;
    if (product.rules && !showGenericModal) {
      mandatoryModal = checkMandatoryModal({
        productRules: product.rules,
        genericModalWhen: INFO_GENERIC_RULES_WHEN.after_patient_identification
      });
    }

    if (showGenericModal || !mandatoryModal) {
      await handleSchedulePatient({
        patient,
        product,
        showGenericModal: !!showGenericModal
      });
    }
    setLoading(false);
  }

  function updateFormPatient(patient: PatientProps) {
    formik.setValues({
      ...patient,
      document: patient.documentType === 'CPF' ? maskCpf(patient.document) : maskPassport(patient.document),
      phone: maskPhone(patient.phone),
      birthDateDate: maskDateStringIso(patient.birthDate) || '',
      pregnant: patient.gender === 'Feminino' ? (patient.pregnant ? 'Sim' : 'Não') : '',
      gender: patient.gender === 'não informado' ? '' : patient.gender,
      idPatient: patient.idPatient || ''
    });
  }

  function validateGenderCatalogGenericRules(gender?: string) {
    const productSelected = findProductOriginal(scheduleSelected?.productId || '');
    const genericRuleCatalogGender = hasGenericRuleCatalog('gender', productSelected?.rules);

    const genderCheck = gender || formik.values.gender;

    return genericRuleCatalogGender
      ? validateGenericRules(genericRuleCatalogGender, genderCheck === 'Feminino' ? 'female' : 'male')
      : true;
  }

  async function checkPatientApsServices(params: { document: string; documentType: string }) {
    try {
      const products = await validateApsEligibility(params);
      setApsProducts(products);
      goTo(PAGES.APS_PRODUCTS.id);
    } catch (error) {
      setIsModalSystemUnavailableVisible(true);
    }
  }

  return {
    updateFormPatient,
    loading,
    formik,
    setLoading,
    genericModal,
    patientSelected,
    selectedProduct,
    routeProductSelector,
    handleSchedulePatient,
    validateGenderCatalogGenericRules,
    isModalSystemUnavailableVisible,
    setIsModalSystemUnavailableVisible,
    isModalNoEligibility,
    setIsModalNoEligibility,
    isModalProductNotCoveredVisible,
    setIsModalProductNotCoveredVisible
  };
}
